// Form2BAuditLogReport.jsx
import React, { useEffect, useMemo, useState } from 'react';
import LayoutWithNavbar from './LayoutWithNavbar';
import { fetchStates, fetchForm2BAuditLogs } from '../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Form2BAuditLogReport = () => {
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(''); // MMM-YYYY
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load states
  useEffect(() => {
    const loadStates = async () => {
      const res = await fetchStates();
      if (res.success) setStates(res.data);
    };
    loadStates();
  }, []);


  const monthOptions = useMemo(() => {
    const start = new Date(2025, 0, 1); // Jan 2025
    const now = new Date();
    const out = [];
    const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' });
    let cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 1);

    while (cur <= last) {
      const [mon, yr] = fmt.format(cur).split(' ');
      out.push(`${mon}-${yr}`); // e.g., Aug-2025
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    return out;
  }, []);

  const handleSearch = async () => {
    if (!selectedMonth || !selectedState) {
      alert('Please select both month and state');
      return;
    }
    setLoading(true);

    const res = await fetchForm2BAuditLogs({ month: selectedMonth, state: selectedState });
    if (res.success) setReportData(res.data);
    setLoading(false);
  };

  const handleExport = () => {
    if (!reportData.length) return alert("No data to export");


    const exportData = reportData.map((row, index) => ({
      'S.No': index + 1,
      'Month': selectedMonth,
      'User': row.user,
      'Upload Date': row.upload_time?.split('T')[0],
      'State': row.state,
      'File Name': row.filename,
      'Total Records': row.total_records,
      'Reconciled': row.reconciled,
      'Unreconciled': row.unreconciled
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Form2B Summary`);
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), `Form2B_AuditLog_Summary_${selectedMonth}.xlsx`);
  };

  return (
    <LayoutWithNavbar>
      <div style={styles.container}>
        <h2 style={styles.heading}>Form-2B Upload Audit Log Report</h2>

        <div style={styles.filterSection}>
          <select value={selectedState} onChange={e => setSelectedState(e.target.value)} style={styles.select}>
            <option value=''>Select State</option>
            {states.map(s => <option key={s.id} value={s.id}>{s.statename}</option>)}
          </select>

          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            style={styles.select}
          >
            <option value=''>Select Month</option>
            {monthOptions.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          <button onClick={handleSearch} style={styles.searchButton}>Search</button>
          <button onClick={handleExport} style={styles.exportButton}>Export</button>
        </div>

        {loading ? (
          <div style={styles.loaderWrapper}><p>Loading data...</p></div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['S.No', 'User', 'Upload Date', 'State', 'File Name', 'Total Records', 'Reconciled', 'Unreconciled'].map((col, i) => (
                    <th key={i} style={styles.th}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => (
                  <tr
                    key={index}
                    style={{ ...styles.tr, cursor: 'pointer' }}
                    onClick={() => (window.location.href = `/form2b-auditlog-details/${row.id}`)}
                  >
                    <td style={styles.td}>{index + 1}</td>
                    <td style={styles.td}>{row.user}</td>
                    <td style={styles.td}>{row.upload_time.split('T')[0]}</td>
                    <td style={styles.td}>{row.state}</td>
                    <td style={styles.td}>{row.filename}</td>
                    <td style={styles.td}>{row.total_records}</td>
                    <td style={styles.td}>{row.reconciled}</td>
                    <td style={styles.td}>{row.unreconciled}</td>
                  </tr>
                ))}
                {!reportData.length && (
                  <tr>
                    <td colSpan={8} style={{ ...styles.td, textAlign: 'center', fontStyle: 'italic' }}>
                      No records found. Try a different month/state.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </LayoutWithNavbar>
  );
};

const styles = {
  container: { padding: '30px', backgroundColor: '#f8f9fa', minHeight: '100vh', fontFamily: 'Segoe UI, sans-serif' },
  heading: { fontSize: '24px', fontWeight: '600', marginBottom: '20px' },
  filterSection: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '25px',
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 0 10px rgba(0,0,0,0.05)'
  },
  select: { padding: '16px', fontSize: '18px', border: '1px solid #ccc', borderRadius: '6px', minWidth: '240px' },
  searchButton: {
    padding: '10px 20px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  exportButton: {
    padding: '10px 20px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer'
  },
  loaderWrapper: { textAlign: 'center', marginTop: '40px' },
  tableWrapper: {
    overflowX: 'auto',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 0 10px rgba(0,0,0,0.04)'
  },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '1200px' },
  th: {
    padding: '12px',
    backgroundColor: '#343a40',
    color: '#fff',
    fontWeight: '800',
    textAlign: 'left',
    borderBottom: '1px solid #dee2e6'
  },
  tr: {
    backgroundColor: '#fff',
    transition: 'background-color 0.2s'
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #dee2e6',
    fontSize: '16px'
  }
};

export default Form2BAuditLogReport;
